import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { 
  ShieldOff, 
  Lock, 
  CreditCard, 
  Phone, 
  Mail,
  ArrowRight,
  LogOut,
  MessageCircle
} from 'lucide-react';

const AccountBlocked = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigateToPlans = () => {
    // Navigate to public plans page
    navigate('/planos');
  };

  const handleWhatsAppSupport = () => {
    // WhatsApp link with pre-defined message
    const phone = '5534992004667';
    const message = encodeURIComponent('Olá, minha conta no CERP Brasil foi suspensa e gostaria de ajuda para reativar.');
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  const handleEmailSupport = () => {
    // Open email client
    const subject = encodeURIComponent('Reativação de Conta - CERP Brasil');
    const body = encodeURIComponent('Olá,\n\nMinha conta no CERP Brasil foi suspensa e gostaria de ajuda para reativar.\n\nAguardo retorno.');
    window.location.href = `mailto:suporte@cerpbrasil.com.br?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full shadow-xl" data-testid="account-blocked-page">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <ShieldOff className="h-10 w-10 text-red-600" />
          </div>
          <CardTitle className="text-2xl text-[#1e3a5f]">
            Conta Bloqueada
          </CardTitle>
          <CardDescription className="text-base mt-2">
            O acesso à sua conta foi suspenso
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Lock className="h-5 w-5 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Por que minha conta foi bloqueada?</p>
                <p>
                  Sua assinatura expirou ou foi cancelada e o período de acesso terminou. 
                  Para continuar utilizando o CERP Brasil NR1, é necessário contratar um plano.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-[#1e3a5f]">O que você pode fazer:</h4>
            
            <div className="space-y-2">
              {/* Contratar Plano - Clickable */}
              <button
                onClick={handleNavigateToPlans}
                className="w-full flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer text-left"
                data-testid="contract-plan-card"
              >
                <CreditCard className="h-5 w-5 text-[#0d9488]" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">Contratar um Plano</p>
                  <p className="text-xs text-slate-500">Reative seu acesso escolhendo um plano</p>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </button>
              
              {/* WhatsApp Support - Clickable */}
              <button
                onClick={handleWhatsAppSupport}
                className="w-full flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-green-50 transition-colors cursor-pointer text-left group"
                data-testid="whatsapp-support-card"
              >
                <MessageCircle className="h-5 w-5 text-[#0d9488] group-hover:text-green-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800 group-hover:text-green-700">Falar com Suporte (WhatsApp)</p>
                  <p className="text-xs text-slate-500">(34) 99200-4667</p>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-green-500" />
              </button>
              
              {/* Email Support - Clickable */}
              <button
                onClick={handleEmailSupport}
                className="w-full flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer text-left group"
                data-testid="email-support-card"
              >
                <Mail className="h-5 w-5 text-[#0d9488] group-hover:text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800 group-hover:text-blue-700">Enviar Email</p>
                  <p className="text-xs text-slate-500">suporte@cerpbrasil.com.br</p>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-blue-500" />
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-4">
            <Button 
              className="w-full bg-[#0d9488] hover:bg-[#0f766e]"
              onClick={handleNavigateToPlans}
              data-testid="reactivate-btn"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Ver Planos e Reativar
            </Button>
            
            <Button 
              variant="outline"
              className="w-full"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sair da Conta
            </Button>
          </div>

          <p className="text-xs text-center text-slate-500 pt-2">
            Seus dados estão seguros e serão restaurados assim que você reativar sua assinatura.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountBlocked;
